import React, { useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';

interface Step2FilesProps {
    formData: any;
    mainFiles: any[];
    attachedFiles: any[];
    documentTypeItems: any[];
    documentTypeItemsLoading: boolean;
    documentTypeItemMeta: any;
    formTypeOptions: any[];
    isViewMode?: boolean;
    filePreviews: Record<string, string>;
    previewLoading: Record<string, boolean>;
    onComputerUpload: () => void;
    onCreateGoogleFile: () => void;
    onSelectTemplate?: () => void;
    onViewFile: (file: any) => void;
    onRemoveFile: (file: any) => void;
    onItemUpload: (item: any, idx: number) => void;
    onItemMetaChange: (seqKey: string, field: string, value: any) => void;
    validationTriggered?: boolean;
    getFileMenuItems: (file: any) => any;
    getAttachmentMenuItems: (file: any) => any;
    getIconByExtension: (ext: string) => { icon: string; color: string };
    getFileTypeName: (item: any) => string;
    isTemplateMode?: boolean;
    pdfGenerating?: Set<string>;
}

const normalizeFormTypeValue = (val: any) => {
    if (!val) return null;
    if (typeof val === 'object' && val.value) return val.value;
    return val;
};

export const Step2Files: React.FC<Step2FilesProps> = ({
    formData,
    mainFiles,
    attachedFiles,
    documentTypeItems,
    documentTypeItemsLoading,
    documentTypeItemMeta,
    formTypeOptions,
    isViewMode = false,
    validationTriggered = false,
    filePreviews,
    previewLoading,
    onComputerUpload,
    onCreateGoogleFile,
    onSelectTemplate,
    onViewFile,
    onRemoveFile,
    onItemUpload,
    onItemMetaChange,
    getFileMenuItems,
    getAttachmentMenuItems,
    getIconByExtension,
    getFileTypeName,
    isTemplateMode = false,
    pdfGenerating = new Set()
}) => {
    const combinedTypeItems = React.useMemo(() => {
        const items = Array.isArray(documentTypeItems) ? [...documentTypeItems] : [];
        if (Array.isArray(attachedFiles)) {
            attachedFiles.forEach((file: any) => {
                const fileSeqStr = String(file.sequenceNum ?? file.configSequenceNum ?? '');
                const exists = items.find((item: any, idx: number) => {
                    const seq = item.sequenceNum ?? item.configSequenceNum ?? item.agreementItemSeq ?? item.agreementConfigureItemSeq ?? item.id ?? item.value ?? (idx + 1);
                    const itemSeqStr = String(seq ?? '');
                    return itemSeqStr === fileSeqStr;
                });
                if (!exists && fileSeqStr) {
                    items.push({
                        sequenceNum: file.sequenceNum ?? file.configSequenceNum,
                        description: file.name || file.fileName || 'Tệp mẫu đính kèm',
                        fileTypeEnumId: file.agreementFormTypeEnumId || file.contentTypeEnumId || file.fileTypeEnumId || '',
                        isRequire: 'N',
                        isExtraneous: true
                    });
                }
            });
            items.sort((a, b) => {
                const seqA = Number(a.sequenceNum ?? a.configSequenceNum ?? 0);
                const seqB = Number(b.sequenceNum ?? b.configSequenceNum ?? 0);
                return seqA - seqB;
            });
        }
        return items;
    }, [documentTypeItems, attachedFiles]);

    const fileUploadRef = useRef<FileUpload>(null);

    return (
        <div className="grid">
            <div className="col-12">
                <Card title={<span className="font-bold">TỆP TÀI LIỆU ({mainFiles.length})</span>} className="shadow-1 mb-4">
                    <div className="grid mt-2">
                        {!isViewMode && (
                            <div className="col-12 md:col-6 lg:col-4 xl:col-3 mb-3">
                                <div className="border-2 border-dashed border-round p-4 text-center transition-all hover:border-primary"
                                    style={{
                                        backgroundColor: 'var(--surface-variant)',
                                        borderColor: 'var(--primary-color)',
                                        height: '350px',
                                        minHeight: '350px',
                                        borderStyle: 'dashed'
                                    }}>
                                    <div className="flex flex-column align-items-center gap-3 h-full justify-content-center">
                                        <i className="pi pi-upload text-4xl text-blue-500"></i>
                                        <div>
                                            <p className="text-gray-700 m-0 font-medium text-sm">Thêm vào tệp của bạn từ</p>
                                        </div>
                                        <div className="grid w-full">
                                            <div className={onSelectTemplate ? "col-4" : "col-6"}>
                                                <div
                                                    className="flex flex-column align-items-center gap-1 p-2 border-1 border-gray-300 border-round transition-all cursor-pointer hover:bg-white hover:shadow-2"
                                                    onClick={onComputerUpload}
                                                >
                                                    <i className="pi pi-folder text-xl text-blue-500"></i>
                                                    <span className="text-xs font-medium text-gray-700">Máy tính</span>
                                                </div>
                                            </div>
                                            <div className={onSelectTemplate ? "col-4" : "col-6"}>
                                                <div
                                                    className="flex flex-column align-items-center gap-1 p-2 border-1 border-gray-300 border-round transition-all cursor-pointer hover:bg-white hover:shadow-2"
                                                    onClick={onCreateGoogleFile}
                                                >
                                                    <i className="pi pi-file text-xl text-blue-500"></i>
                                                    <span className="text-xs font-medium text-gray-700">Soạn mới</span>
                                                </div>
                                            </div>
                                            {onSelectTemplate && (
                                                <div className="col-4">
                                                    <div
                                                        className="flex flex-column align-items-center gap-1 p-2 border-1 border-gray-300 border-round transition-all cursor-pointer hover:bg-white hover:shadow-2"
                                                        onClick={onSelectTemplate}
                                                    >
                                                        <i className="pi pi-copy text-xl text-orange-500"></i>
                                                        <span className="text-xs font-medium text-gray-700">Từ mẫu</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <small className="text-gray-500 text-xs">Định dạng được hỗ trợ: .pdf, .docx, .doc</small>
                                        </div>
                                        <Button
                                            label="Thêm"
                                            icon="pi pi-plus"
                                            className="p-button-sm"
                                            onClick={onComputerUpload}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {mainFiles.map((file, index) => (
                            <div key={index} className="col-12 md:col-6 lg:col-4 xl:col-3 mb-3">
                                <div className="card p-3 shadow-1 border-round-lg border-1 surface-border" 
                                    style={{ 
                                        backgroundColor: 'var(--surface-card)', 
                                        height: '350px',
                                        minHeight: '350px'
                                    }}>
                                    <div className="flex flex-column h-full">
                                        <div className="flex-1 mb-2 overflow-hidden" style={{ minHeight: 0 }}>
                                            <div className="border-1 border-round p-2 text-center h-full flex align-items-center justify-content-center" style={{ backgroundColor: 'var(--surface-variant)', borderColor: 'var(--surface-border)' }}>
                                                {(() => {
                                                    const fileKey = `${file.name}-${file.googleFileId || file.fileId || file.id}`;
                                                    if (previewLoading[fileKey]) return <i className="pi pi-spin pi-spinner text-2xl text-blue-500" />;
                                                    if (filePreviews[fileKey]) {
                                                        return <img src={filePreviews[fileKey]} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />;
                                                    }


                                                    const ic = getIconByExtension(file.extension);
                                                    return <i className={`${ic.icon} ${ic.color} text-4xl`} />;
                                                })()}

                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 flex align-items-center justify-content-between gap-1 overflow-hidden" style={{ height: '32px' }}>
                                            <div className="flex align-items-center gap-2 flex-1 overflow-hidden">
                                                {(() => {
                                                    const ic = getIconByExtension(file.extension);
                                                    return <i className={`${ic.icon} ${ic.color} flex-shrink-0 text-sm`}></i>;
                                                })()}
                                                <span 
                                                    className="text-sm font-medium" 
                                                    title={file.name}
                                                    style={{ 
                                                        color: 'var(--text-color)',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        flex: 1
                                                    }}
                                                >
                                                    {file.name}
                                                </span>
                                            </div>


                                            <Menu
                                                model={getFileMenuItems(file)}
                                                popup
                                                ref={(el) => { if (el) (file as any).menuRef = el; }}
                                            />
                                            <Button
                                                icon="pi pi-ellipsis-v"
                                                className="p-button-text p-button-sm p-0"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    (file as any).menuRef?.toggle(e);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {formData.agreementType && (
                    <Card title={<span className="font-bold">TỆP ĐÍNH KÈM ({attachedFiles.length})</span>} className="shadow-1">
                        {documentTypeItemsLoading ? (
                            <div className="p-4 text-center"><i className="pi pi-spin pi-spinner mr-2" /> Đang tải cấu hình...</div>
                        ) : combinedTypeItems.length > 0 ? (
                            <div className="overflow-x-auto border-1 surface-border border-round">
                                <table className="w-full text-sm border-collapse text-color" style={{ minWidth: '800px' }}>
                                    <thead className="surface-200">
                                        <tr>
                                            <th className="p-3 text-center" style={{ width: '50px' }}>#</th>
                                            <th className="p-3 text-left" style={{ width: '200px' }}>Mô tả tài liệu</th>
                                            <th className="p-3 text-left">Tệp đính kèm</th>
                                            <th className="p-3 text-center" style={{ width: '150px' }}>Hình thức</th>
                                            <th className="p-3 text-center" style={{ width: '150px' }}>Ngày nhận</th>
                                        </tr>
                                    </thead>
                                    <tbody className="surface-card">
                                        {combinedTypeItems.map((item, idx) => {
                                            const seq = item.sequenceNum ?? item.configSequenceNum ?? (idx + 1);
                                            const seqKey = String(seq);
                                            const meta = documentTypeItemMeta[seqKey] || {};
                                            const file = attachedFiles.find(f => Number(f.sequenceNum ?? f.configSequenceNum) === Number(seq));
                                            const isRequired = item.isRequire === 'Y' || item.isRequired === 'Y' || item.required === 'Y' || item.isRequire === true || item.isRequired === true || item.required === true;

                                            const currentFormType = meta.formType ?? (file?.agreementFormTypeEnumId ? normalizeFormTypeValue(file.agreementFormTypeEnumId) : null);
                                            const currentReceivedDate = meta.receivedDate ?? (file?.agreementFormDate ? new Date(file.agreementFormDate) : null);

                                            const isFormTypeInvalid = validationTriggered && (isRequired || file) && !currentFormType;
                                            const isReceivedDateInvalid = validationTriggered && currentFormType === 'ATTACH_SCAN' && !currentReceivedDate;

                                            return (
                                                <tr key={seqKey} className="border-bottom-1 surface-border">
                                                    <td className="p-3 text-center font-bold">{seq}</td>
                                                    <td className="p-3">
                                                        {getFileTypeName(item)} {isRequired && <span className="text-red-500">*</span>}
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex align-items-center gap-2">
                                                            {!isViewMode && !file && (
                                                                <Button label="Chọn tệp" icon="pi pi-folder" text size="small" onClick={() => onItemUpload(item, idx)} />
                                                            )}
                                                            <div className="flex flex-column flex-1 overflow-hidden">
                                                                <span className="truncate text-color-secondary" title={file?.name}>{file?.name || 'Chưa chọn tệp'}</span>
                                                            </div>
                                                            {file && (
                                                                <div className="flex gap-1">
                                                                    <Menu
                                                                        model={getAttachmentMenuItems(file)}
                                                                        popup
                                                                        ref={(el) => { if (el) (file as any).attachmentMenuRef = el; }}
                                                                    />
                                                                    <Button
                                                                        icon="pi pi-ellipsis-v"
                                                                        className="p-button-text p-button-secondary p-button-sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            (file as any).attachmentMenuRef?.toggle(e);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <Dropdown 
                                                            value={currentFormType} 
                                                            options={formTypeOptions} 
                                                            onChange={(e) => onItemMetaChange(seqKey, 'formType', e.value)} 
                                                            placeholder="Chọn" 
                                                            className={`w-full p-inputtext-sm ${isFormTypeInvalid ? 'p-invalid' : ''}`} 
                                                            disabled={isViewMode} 
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <Calendar 
                                                            value={currentReceivedDate} 
                                                            onChange={(e) => onItemMetaChange(seqKey, 'receivedDate', e.value)} 
                                                            showIcon 
                                                            dateFormat="dd/mm/yy" 
                                                            placeholder="Chọn ngày" 
                                                            className={`w-full p-inputtext-sm ${isReceivedDateInvalid ? 'p-invalid' : ''}`} 
                                                            disabled={isViewMode} 
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-4 text-center text-gray-500">Loại tài liệu này không có cấu hình file đính kèm.</div>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
};
